/**
 * usePageSections — Local state management for the page editor's sections array.
 *
 * Manages add/reorder/delete operations, localStorage draft persistence,
 * and produces clean SectionEnvelope[] for PostMessage/publish.
 *
 * Supports nested sections (children) for container-frame widgets.
 * Tree operations use pure utilities from @zaviago/storefront-config.
 *
 * Each section gets a transient `_editorId` (crypto.randomUUID) for @dnd-kit
 * stable IDs. These are stripped before sending to the backend.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { arrayMove } from '@dnd-kit/sortable'
import {
  WIDGET_REGISTRY,
  MAX_CONTAINER_DEPTH,
  findSectionByEditorId,
  removeSectionByEditorId,
  addChildToSection,
  reorderChildrenInSection,
  updateSectionByEditorId,
  getSectionDepth,
  type BlockNode,
  type SectionEnvelope,
  type SectionLookup,
} from '@zaviago/storefront-config'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface EditorSection extends SectionEnvelope {
  _editorId: string
  children?: EditorSection[]
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Appearance prop keys (underscore-prefixed in editor props) */
const APPEARANCE_KEYS: Record<string, keyof NonNullable<SectionEnvelope['appearance']>> = {
  _bg: 'bg',
  _textColor: 'textColor',
  _padding: 'padding',
  _radius: 'radius',
  _maxWidth: 'maxWidth',
}

/** Reverse map: appearance key → underscore-prefixed prop key */
const APPEARANCE_TO_PROP: Record<string, string> = Object.fromEntries(
  Object.entries(APPEARANCE_KEYS).map(([prop, app]) => [app, prop]),
)

/**
 * Deep-merge an appearance patch into existing appearance.
 * Handles `responsive.tablet/mobile` sub-objects with null-as-delete semantics:
 *   - `{ responsive: { tablet: { padding: 'sm' } } }` → sets tablet padding
 *   - `{ responsive: { tablet: { padding: null } } }` → deletes tablet padding
 *   - Empty breakpoint objects are removed; empty `responsive` is removed.
 */
function deepMergeAppearance(
  existing: SectionEnvelope['appearance'],
  patch: Record<string, unknown>,
): SectionEnvelope['appearance'] {
  const result: Record<string, unknown> = { ...(existing ?? {}) }

  for (const [key, value] of Object.entries(patch)) {
    if (key === 'responsive' && typeof value === 'object' && value !== null) {
      const existingResponsive = (result.responsive ?? {}) as Record<string, Record<string, unknown>>
      const patchResponsive = value as Record<string, Record<string, unknown>>
      const mergedResponsive: Record<string, Record<string, unknown>> = { ...existingResponsive }

      for (const bp of ['tablet', 'mobile'] as const) {
        if (patchResponsive[bp]) {
          const merged = { ...(existingResponsive[bp] ?? {}), ...patchResponsive[bp] }
          // Remove null keys (delete signal)
          for (const [k, v] of Object.entries(merged)) {
            if (v === null) delete merged[k]
          }
          if (Object.keys(merged).length > 0) {
            mergedResponsive[bp] = merged
          } else {
            delete mergedResponsive[bp]
          }
        }
      }

      if (Object.keys(mergedResponsive).length > 0) {
        result.responsive = mergedResponsive
      } else {
        delete result.responsive
      }
    } else {
      result[key] = value
    }
  }

  return Object.keys(result).length > 0
    ? result as SectionEnvelope['appearance']
    : undefined
}

/** Assign transient _editorId to sections (recursive for children). */
function assignEditorIds(sections: SectionEnvelope[]): EditorSection[] {
  return sections.map((s) => {
    // Hydrate _-prefixed props from appearance so controls display current values
    const extraProps: Record<string, unknown> = {}
    if (s.appearance) {
      for (const [appKey, value] of Object.entries(s.appearance)) {
        const propKey = APPEARANCE_TO_PROP[appKey]
        if (propKey && value !== undefined) {
          extraProps[propKey] = value
        }
      }
    }
    // Hydrate defaultBlocks for existing sections that were saved before block editing
    const widget = WIDGET_REGISTRY[s.type]
    const blocks = (!s.blocks || s.blocks.length === 0) && widget?.defaultBlocks
      ? structuredClone(widget.defaultBlocks)
      : s.blocks

    // Migration: map old exogro-header props into freshly hydrated defaultBlocks (Plan 147, Epic 02)
    if (s.type === 'code:exogro-header' && blocks && (!s.blocks || s.blocks.length === 0)) {
      const props = s.props as Record<string, unknown>
      if (props.announcementText) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'announcement-text')
        if (b && b.content.type === 'text') b.content.value = props.announcementText as string
      }
      if (props.logoUrl) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'topbar-logo')
        if (b && b.content.type === 'image') b.content.src = props.logoUrl as string
      }
      if (props.brandColor) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'navbar-categories')
        if (b && b.content.type === 'category-nav') b.content.brandColor = props.brandColor as string
      }
      if (props.lineId) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'topbar-line-text')
        if (b && b.content.type === 'text') {
          b.content.value = `ติดต่อทีมงานแอด LINE\nLine ID : ${props.lineId}`
        }
      }
      if (props.categoryButtonLabel) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'navbar-categories')
        if (b && b.content.type === 'category-nav') b.content.buttonLabel = props.categoryButtonLabel as string
      }
    }

    // Migration: map old exogro-footer props into freshly hydrated defaultBlocks (Plan 147, Epic 03)
    if (s.type === 'code:exogro-footer' && blocks && (!s.blocks || s.blocks.length === 0)) {
      const props = s.props as Record<string, unknown>
      if (props.logoUrl) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'footer-logo')
        if (b && b.content.type === 'image') b.content.src = props.logoUrl as string
      }
      if (props.tagline) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'footer-tagline')
        if (b && b.content.type === 'text') b.content.value = props.tagline as string
      }
      if (props.address) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'footer-address')
        if (b && b.content.type === 'text') b.content.value = props.address as string
      }
      if (props.phone) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'contact-phone')
        if (b && b.content.type === 'contact-item') b.content.value = props.phone as string
      }
      if (props.fax) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'contact-fax')
        if (b && b.content.type === 'contact-item') b.content.value = props.fax as string
      }
      if (props.email) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'contact-email')
        if (b && b.content.type === 'contact-item') b.content.value = props.email as string
      }
      if (props.lineId) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'contact-line')
        if (b && b.content.type === 'contact-item') b.content.value = props.lineId as string
        // Also update social-link with full LINE URL
        const sl = blocks.find((bl: { id: string }) => bl.id === 'footer-social-line')
        if (sl && sl.content.type === 'social-link') {
          sl.content.url = `https://line.me/R/ti/p/${props.lineId}`
        }
      }
      if (props.facebookUrl) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'footer-social-facebook')
        if (b && b.content.type === 'social-link') b.content.url = props.facebookUrl as string
      }
      if (props.youtubeUrl) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'footer-social-youtube')
        if (b && b.content.type === 'social-link') b.content.url = props.youtubeUrl as string
      }
      if (props.companyName) {
        const b = blocks.find((bl: { id: string }) => bl.id === 'copyright-text')
        if (b && b.content.type === 'text') {
          b.content.value = `© ${new Date().getFullYear()} ${props.companyName}. All Rights Reserved`
        }
      }
    }

    // Hydrate groupId for blocks (Plan 148 — cross-group block reorder)
    if (blocks) {
      for (const block of blocks) {
        if (!block.layout.groupId) {
          block.layout.groupId = block.id.split('-')[0]
        }
      }
    }

    const { children: rawChildren, ...rest } = s
    return {
      ...rest,
      blocks,
      props: { ...s.props, ...extraProps },
      _editorId: crypto.randomUUID(),
      children: rawChildren?.length ? assignEditorIds(rawChildren) : undefined,
    }
  })
}

/** Strip transient _editorId and extract appearance props (recursive for children).
 *  Note: blocks field passes through via rest spread — no special handling needed. */
function stripEditorIds(sections: EditorSection[]): SectionEnvelope[] {
  return sections.map(({ _editorId: _, ...rest }) => {
    // Extract _-prefixed appearance props into the appearance object
    const appearance: Record<string, unknown> = { ...rest.appearance }
    const cleanProps: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(rest.props)) {
      const appearanceKey = APPEARANCE_KEYS[key]
      if (appearanceKey) {
        if (value !== undefined) {
          appearance[appearanceKey] = value
        } else {
          delete appearance[appearanceKey]
        }
      } else {
        cleanProps[key] = value
      }
    }

    const hasAppearance = Object.keys(appearance).length > 0
    const envelope: SectionEnvelope = {
      ...rest,
      props: cleanProps,
      appearance: hasAppearance ? appearance as SectionEnvelope['appearance'] : undefined,
    }
    // Recursively strip children
    if (rest.children && rest.children.length > 0) {
      envelope.children = stripEditorIds(rest.children as EditorSection[])
    } else {
      delete envelope.children
    }
    return envelope
  })
}

/** Get the max nesting depth below a section (0 for leaf nodes). */
function getMaxDepthBelow(section: { children?: { children?: unknown }[] }): number {
  if (!section.children?.length) return 0
  let max = 0
  for (const child of section.children) {
    max = Math.max(max, 1 + getMaxDepthBelow(child as typeof section))
  }
  return max
}

function draftKey(pageId: string, tenantId: string) {
  return `page-editor-draft-${tenantId}-${pageId}`
}

function loadDraft(pageId: string, tenantId: string): EditorSection[] | null {
  try {
    const raw = localStorage.getItem(draftKey(pageId, tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return null
    // Re-assign IDs on restore (IDs are transient per session)
    return assignEditorIds(parsed as SectionEnvelope[])
  } catch {
    return null
  }
}

function saveDraft(pageId: string, tenantId: string, sections: EditorSection[]) {
  try {
    localStorage.setItem(draftKey(pageId, tenantId), JSON.stringify(stripEditorIds(sections)))
  } catch {
    // localStorage full or unavailable — ignore
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePageSections(
  pageId: string,
  tenantId: string,
  initialSections: SectionEnvelope[] | null,
) {
  const hydratedRef = useRef(false)
  const hasDraftRef = useRef(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const savedRevisionRef = useRef(0)

  // Initialize: try localStorage draft first, then server sections
  const [sections, setSections] = useState<EditorSection[]>(() => {
    if (!tenantId) return initialSections ? assignEditorIds(initialSections) : []
    const draft = loadDraft(pageId, tenantId)
    if (draft && draft.length > 0) {
      hydratedRef.current = true
      hasDraftRef.current = true
      return draft
    }
    if (initialSections) {
      hydratedRef.current = true
      return assignEditorIds(initialSections)
    }
    return []
  })

  // Revision counter for O(1) isDirty — starts at 1 if draft was loaded (dirty)
  const [revision, setRevision] = useState(() => (hasDraftRef.current ? 1 : 0))

  // Sync when initialSections arrives (null → populated) — only once
  useEffect(() => {
    if (hydratedRef.current) return
    if (!initialSections) return
    hydratedRef.current = true
    // Only hydrate if no local state yet
    setSections((prev) => (prev.length === 0 ? assignEditorIds(initialSections) : prev))
  }, [initialSections])

  // Auto-save to localStorage on change (debounced 500ms)
  useEffect(() => {
    clearTimeout(saveTimerRef.current)
    if (!tenantId) return // Don't save drafts without tenant context
    saveTimerRef.current = setTimeout(() => saveDraft(pageId, tenantId, sections), 500)
    return () => clearTimeout(saveTimerRef.current)
  }, [sections, pageId, tenantId])

  // --- Root-level actions (unchanged API) ---

  const addSection = useCallback((type: string): string | null => {
    const widget = WIDGET_REGISTRY[type]
    if (!widget) {
      console.warn(`[usePageSections] Unknown widget type: ${type}`)
      return null
    }
    const envelope = structuredClone(widget.defaults)
    // Hydrate blocks from defaultBlocks if the widget supports block editing
    if (widget.defaultBlocks && !envelope.blocks) {
      envelope.blocks = structuredClone(widget.defaultBlocks)
    }
    const editorSection = assignEditorIds([envelope])[0]
    setSections((prev) => [...prev, editorSection])
    setRevision((r) => r + 1)
    return editorSection._editorId
  }, [])

  /** Add a section with pre-populated blocks at a specific index (Plan 149 — cross-section gap drop). */
  const addSectionWithBlocks = useCallback(
    (type: string, insertIndex: number, initialBlocks: BlockNode[]): string | null => {
      const widget = WIDGET_REGISTRY[type]
      if (!widget) {
        console.warn(`[usePageSections] Unknown widget type: ${type}`)
        return null
      }
      const envelope = structuredClone(widget.defaults)
      envelope.blocks = initialBlocks
      const editorSection = assignEditorIds([envelope])[0]
      setSections((prev) => {
        const next = [...prev]
        next.splice(insertIndex, 0, editorSection)
        return next
      })
      setRevision((r) => r + 1)
      return editorSection._editorId
    },
    [],
  )

  /** Reorder root-level sections only. For children, use reorderChildren. */
  const reorderSections = useCallback((activeId: string, overId: string) => {
    setSections((prev) => {
      const oldIndex = prev.findIndex((s) => s._editorId === activeId)
      const newIndex = prev.findIndex((s) => s._editorId === overId)
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
    setRevision((r) => r + 1)
  }, [])

  // --- Tree-aware actions ---

  /** Add a widget as a child of a container section. Enforces depth limit. Returns the new child's editorId on success, null on failure. */
  const addChildSection = useCallback((parentEditorId: string, type: string): string | null => {
    const widget = WIDGET_REGISTRY[type]
    if (!widget) {
      console.warn(`[usePageSections] Unknown widget type: ${type}`)
      return null
    }
    const childId = crypto.randomUUID()
    let added = false
    setSections((prev) => {
      const parentDepth = getSectionDepth(prev, parentEditorId)
      if (parentDepth < 0 || parentDepth + 1 >= MAX_CONTAINER_DEPTH) {
        console.warn(`[usePageSections] Cannot add child: depth limit reached`)
        return prev
      }
      const envelope = structuredClone(widget.defaults)
      // Hydrate blocks from defaultBlocks if the widget supports block editing
      if (widget.defaultBlocks && !envelope.blocks) {
        envelope.blocks = structuredClone(widget.defaultBlocks)
      }
      const child: EditorSection = {
        ...envelope,
        props: { ...envelope.props },
        _editorId: childId,
        children: undefined,
      }
      added = true
      return addChildToSection(prev, parentEditorId, child)
    })
    if (added) {
      setRevision((r) => r + 1)
      return childId
    }
    return null
  }, [])

  /** Remove a section at any depth (recursive find-and-remove). */
  const removeSection = useCallback((editorId: string) => {
    setSections((prev) => removeSectionByEditorId(prev, editorId))
    setRevision((r) => r + 1)
  }, [])

  /** Reorder children within a specific container. */
  const reorderChildren = useCallback((parentEditorId: string, activeId: string, overId: string) => {
    setSections((prev) => reorderChildrenInSection(prev, parentEditorId, activeId, overId))
    setRevision((r) => r + 1)
  }, [])

  /** Update a section at any depth (recursive find-and-update). */
  const updateSection = useCallback(
    (editorId: string, patch: Partial<SectionEnvelope>) => {
      setSections((prev) =>
        updateSectionByEditorId(prev, editorId, (s) => ({
          ...s,
          props: { ...s.props, ...(patch.props ?? {}) },
          slots:
            patch.slots !== undefined ? { ...s.slots, ...patch.slots } : s.slots,
          appearance:
            patch.appearance !== undefined
              ? deepMergeAppearance(s.appearance, patch.appearance as Record<string, unknown>)
              : s.appearance,
          // blocks field passes through when provided in the patch
          blocks: patch.blocks !== undefined ? patch.blocks : s.blocks,
        })),
      )
      setRevision((r) => r + 1)
    },
    [],
  )

  /** Move a section into a container's children. Enforces depth limit. */
  const moveToContainer = useCallback((editorId: string, targetContainerId: string) => {
    setSections((prev) => {
      const lookup = findSectionByEditorId(prev, editorId)
      if (!lookup) return prev
      // Depth validation: target depth + 1 (for the moved section) + its subtree depth
      const targetDepth = getSectionDepth(prev, targetContainerId)
      const subtreeDepth = getMaxDepthBelow(lookup.section as EditorSection)
      if (targetDepth + 1 + subtreeDepth >= MAX_CONTAINER_DEPTH) {
        console.warn('[usePageSections] Cannot move: would exceed depth limit')
        return prev
      }
      const after = removeSectionByEditorId(prev, editorId)
      return addChildToSection(after, targetContainerId, lookup.section as EditorSection)
    })
    setRevision((r) => r + 1)
  }, [])

  /** Move a child section to root level. */
  const moveToRoot = useCallback((editorId: string) => {
    setSections((prev) => {
      const lookup = findSectionByEditorId(prev, editorId)
      if (!lookup || !lookup.parent) return prev // already at root
      const after = removeSectionByEditorId(prev, editorId)
      return [...after, lookup.section as EditorSection]
    })
    setRevision((r) => r + 1)
  }, [])

  /** Duplicate a section (and its children) at the same level. Returns new editorId or null. */
  const duplicateSection = useCallback((editorId: string): string | null => {
    let newId: string | null = null
    setSections((prev) => {
      const lookup = findSectionByEditorId(prev, editorId)
      if (!lookup) return prev
      // Deep clone with fresh IDs via strip→assign round-trip
      const cloned = assignEditorIds(stripEditorIds([lookup.section as EditorSection]))
      if (cloned.length === 0) return prev
      const clone = cloned[0]
      newId = clone._editorId

      if (lookup.parent) {
        // Insert after original in parent's children
        const parentSection = lookup.parent as EditorSection
        return updateSectionByEditorId(prev, parentSection._editorId, (p) => {
          const children = (p.children as EditorSection[]) ?? []
          const idx = children.findIndex((c) => c._editorId === editorId)
          const newChildren = [...children]
          newChildren.splice(idx + 1, 0, clone)
          return { ...p, children: newChildren } as typeof p
        })
      } else {
        // Insert after original at root
        const idx = prev.findIndex((s) => s._editorId === editorId)
        const result = [...prev]
        result.splice(idx + 1, 0, clone)
        return result
      }
    })
    if (newId) setRevision((r) => r + 1)
    return newId
  }, [])

  /** Reorder blocks within a section's group by swapping layout.order values. */
  const reorderBlocks = useCallback(
    (editorId: string, groupId: string, activeBlockId: string, overBlockId: string) => {
      setSections((prev) =>
        updateSectionByEditorId(prev, editorId, (s) => {
          if (!s.blocks) return s
          // Split blocks into target group and other groups
          const inGroup = s.blocks
            .filter((b) => groupId === '_flat' || b.layout.groupId === groupId)
            .sort((a, b) => a.layout.order - b.layout.order)
          const otherBlocks = groupId === '_flat'
            ? []
            : s.blocks.filter((b) => b.layout.groupId !== groupId)
          const oldIdx = inGroup.findIndex((b) => b.id === activeBlockId)
          const newIdx = inGroup.findIndex((b) => b.id === overBlockId)
          if (oldIdx === -1 || newIdx === -1 || oldIdx === newIdx) return s
          const reordered = arrayMove(inGroup, oldIdx, newIdx)
          return {
            ...s,
            blocks: [
              ...otherBlocks,
              ...reordered.map((b, i) => ({
                ...b,
                layout: { ...b.layout, order: i },
              })),
            ],
          }
        }),
      )
      setRevision((r) => r + 1)
    },
    [],
  )

  /** Find a section by editorId. For stable references, use findSectionByEditorId directly. */
  const findByEditorId = useCallback(
    (editorId: string): SectionLookup<EditorSection> | null => {
      return findSectionByEditorId(sections, editorId) as SectionLookup<EditorSection> | null
    },
    [sections],
  )

  // --- Derived ---

  const toEnvelopes = useCallback((): SectionEnvelope[] => {
    return stripEditorIds(sections)
  }, [sections])

  const isDirty = revision !== savedRevisionRef.current

  const clearDraft = useCallback(() => {
    savedRevisionRef.current = revision
    if (!tenantId) return
    try {
      localStorage.removeItem(draftKey(pageId, tenantId))
    } catch {
      // ignore
    }
  }, [pageId, tenantId, revision])

  return {
    sections,
    addSection,
    addSectionWithBlocks,
    addChildSection,
    removeSection,
    reorderSections,
    reorderChildren,
    reorderBlocks,
    updateSection,
    moveToContainer,
    moveToRoot,
    duplicateSection,
    findByEditorId,
    toEnvelopes,
    isDirty,
    clearDraft,
  }
}
